import useSWR, { mutate as swrMutate } from 'swr';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { Server } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/contexts/I18nContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  getZoneLocalComputer,
  SETUP_STAGES_ORDERED,
  type LocalComputerSummary,
} from '@/api/localComputers';
import { LocalComputerDiscoveryPanel } from './LocalComputerDiscoveryPanel';

interface Props {
  zoneId: number | string;
  /** Fires after a peer is imported (camera / NVR / router). Used by
   *  the equipment-tab parent to refresh the sibling card/lists. */
  onImported?: () => void;
}

/**
 * Read-only "Компьютер Linza" panel on the Equipment page.
 * Shows the LocalComputer bound to this zone's deployment, or hides itself if
 * the zone has none (e.g. server-side-pull-only branches).
 *
 * No system-level controls — that's the superuser admin page (/admin/local-computers).
 *
 * **Render contract** (per 2026-05-17-equipment-autodiscovery-flow.md §7):
 *   - 404 / no LC bound → render null.
 *   - Initial load (no data, no error yet) → render null.
 *     Without this, the card would briefly flash on every branch open and
 *     then disappear on 404 — exactly the "Компьютер Линза flash" bug.
 *   - Loaded data → render the panel.
 *   - Non-404 error → render the card with an inline error banner (the user
 *     deserves to know we couldn't tell whether the zone has an LC).
 */
export default function LocalComputerPanel({ zoneId, onImported }: Props) {
  const { t } = useT();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const swrKey = `zone-local-computer:${zoneId}`;
  const { data, error, isLoading } = useSWR<LocalComputerSummary, { response?: { status?: number } }>(
    swrKey,
    () => getZoneLocalComputer(zoneId).then(r => r.data),
    { shouldRetryOnError: false },
  );

  // 404 = no LocalComputer for this branch (server-pull-only). Hide silently.
  const status404 = error?.response?.status === 404;
  if (status404) return null;

  // Initial load — render nothing rather than flashing a Card+Loader that
  // disappears on 404. Fast in practice (<500ms typical).
  if (isLoading && !data && !error) return null;

  // Single derivation of liveness so the badge in the header and the
  // PanelBody agree. Today: DOCKER_RUNNING + not archived → "Работает".
  // TODO: when Prometheus integration lands on the FE, swap this for a
  // real heartbeat check (the BE's last_heartbeat_at was dropped in
  // migration 0004 — Prometheus replaces). Currently a jetson that
  // finished setup but is offline still shows "Работает".
  const liveness = data
    ? data.is_archived
      ? { label: t('admin.lc.archived'), variant: 'muted' as const }
      : data.setup_stage === 'DOCKER_RUNNING'
        ? { label: t('admin.lc.live'), variant: 'success' as const }
        : { label: t('admin.lc.notReady'), variant: 'warning' as const }
    : null;

  return (
    <Card>
      <CardContent className="space-y-4">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
          <Server className="size-4" />
          <span>{t('equipment.lc.title')}</span>
          {liveness && (
            <Badge variant={liveness.variant} className="ml-1">
              {liveness.label}
            </Badge>
          )}
        </h2>

        {error && !status404 && !data && <ServerStatusNotice variant="card" error={error} />}

        {data && <PanelBody lc={data} t={t} isSuperuser={isSuperuser} />}

        {/* Peer-discovery panel (2026-06-02 Group D redesign).
            Replaces the prior wizard-style `<DiscoveryPanel>` slot —
            same `subnet-scan` primitive underneath, but the operator
            now drives every per-host action explicitly through the same
            `<DiscoveredPeerList>` the Router card uses. Still
            superuser-only until staging soak is done. The legacy
            `<DiscoveryPanel>` stays in the codebase because
            `SetupWizardPage` still mounts it for first-time setup. */}
        {data && isSuperuser && (
          <LocalComputerDiscoveryPanel
            lcId={data.id}
            zoneId={zoneId}
            onImported={() => {
              // The new peer flow can plant any of camera / NVR /
              // router, so refresh every sibling SWR key.
              void swrMutate(`zone-router:${zoneId}`);
              void swrMutate(`zone-nvrs:${zoneId}`);
              onImported?.();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function PanelBody({
  lc,
  t,
  isSuperuser,
}: {
  lc: LocalComputerSummary;
  t: (k: string, p?: Record<string, string | number>) => string;
  isSuperuser: boolean;
}) {
  const stageIdx = SETUP_STAGES_ORDERED.indexOf(lc.setup_stage);
  const stageTotal = SETUP_STAGES_ORDERED.length;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium">{lc.display_name}</span>
        {/* Friendly operator-facing identifier — "#14" reads cleanly
            and lines up with the wiki/W1-Deployments table. Falls
            through when the BE hasn't allocated a device_number yet
            (older LC rows). */}
        {lc.device_number != null && (
          <span className="text-xs text-muted-foreground">#{lc.device_number}</span>
        )}
        {/* Hostname (kit-side serial name like "linza-desktop") +
            hardware model: superuser-only chrome — operators see
            "Surf Coffee Riders Lodge #14" and nothing else. */}
        {isSuperuser && (
          <span className="font-mono text-xs text-muted-foreground">{lc.hostname}</span>
        )}
      </div>

      {/* Setup-stage progress + hardware model are deployment-internals
          and stay superuser-only on this page. The liveness pill in the
          card header already covers the operator-relevant status. */}
      {isSuperuser && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="muted" className="font-normal">
            {t(`admin.lc.stage.${lc.setup_stage}`)}
            <span className="ml-1 tabular-nums text-muted-foreground">
              {stageIdx + 1}/{stageTotal}
            </span>
          </Badge>
          <span>{lc.hardware_model}</span>
        </div>
      )}
    </div>
  );
}

// `formatHeartbeat` helper removed (xxvii / task #82) — BE no longer
// tracks `last_heartbeat_at`.
